import { ObjectUrlCache } from '../gallery/objectUrlCache';
import { requestAccessToken, type AccessToken } from './googleIdentity';
import {
  createSession,
  deleteSession,
  getSession,
  listMediaItems,
  parseDurationMs,
  sizedBaseUrl,
  type PickedMediaItem,
} from './pickerApi';
import {
  FALLBACK_INTEREST,
  FALLBACK_PLACEHOLDER,
  mapWithConcurrency,
  probeImage,
} from './probeImage';
import type { PhotoItem, PhotoSize, PhotoSource, PhotoSourceProgress } from './types';

/**
 * Photos the user picks in Google's own picker UI.
 *
 * Album selection is not possible — Google removed those scopes on 2025-03-31 and the picker
 * doesn't browse albums. What we get is whatever the user explicitly selected. See PLAN.md §1.
 *
 * Verified by Spike S1 (2026-07-25): the browser *can* fetch picked bytes cross-origin with an
 * Authorization header, so this works with no backend.
 */

/** Bytes hold real memory here, unlike local object URLs. Keep the ceiling low. */
const URL_CACHE_SIZE = 120;

/** Tiny enough to be ~1 KB, big enough to average a sensible color from. */
const LQIP_PX = 32;
const LQIP_CONCURRENCY = 6;

/**
 * Requests snap to these sizes so a tile and a full-screen view don't thrash the cache with
 * near-identical URLs — and so opening a photo full-screen actually upgrades it instead of
 * reusing the small tile version.
 */
const SIZE_BUCKETS = [320, 640, 1280, 2048] as const;

function bucketFor(size: PhotoSize): number {
  const longest = Math.max(size.w, size.h);
  return SIZE_BUCKETS.find((b) => b >= longest) ?? SIZE_BUCKETS[SIZE_BUCKETS.length - 1];
}

/** Treat the token as dead slightly early rather than firing doomed requests. */
const EXPIRY_MARGIN_MS = 60_000;

export class TokenExpiredError extends Error {
  constructor() {
    super('Your Google session expired. Reconnect to keep browsing.');
    this.name = 'TokenExpiredError';
  }
}

export interface GooglePhotosSourceOptions {
  clientId: string;
  /**
   * Called with the picker URL as soon as the session exists. The UI must render this as a
   * link the user clicks: `window.open` after an `await` has lost the user gesture and gets
   * blocked in some browsers.
   */
  onPickerReady?: (pickerUri: string) => void;
}

function metadataDimensions(item: PickedMediaItem): { width: number; height: number } | null {
  const meta = item.mediaFile?.mediaFileMetadata;
  const width = Number(meta?.width);
  const height = Number(meta?.height);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width, height };
  }
  return null;
}

export class GooglePhotosPickerSource implements PhotoSource {
  readonly id = 'google-photos' as const;
  readonly label = 'Google Photos';
  readonly ephemeral = true;

  private items: PhotoItem[] = [];
  private cache = new ObjectUrlCache(URL_CACHE_SIZE);
  private accessToken: AccessToken | null = null;

  constructor(private readonly options: GooglePhotosSourceOptions) {}

  private get token(): string {
    if (!this.accessToken || Date.now() > this.accessToken.expiresAt - EXPIRY_MARGIN_MS) {
      throw new TokenExpiredError();
    }
    return this.accessToken.token;
  }

  /** True once the token is close enough to expiry that resolves will start failing. */
  get expired(): boolean {
    return !this.accessToken || Date.now() > this.accessToken.expiresAt - EXPIRY_MARGIN_MS;
  }

  async connect(signal: AbortSignal, onProgress?: (p: PhotoSourceProgress) => void): Promise<void> {
    const report = (label: string, loaded = 0, total = 0) => onProgress?.({ loaded, total, label });

    report('Waiting for Google authorization');
    this.accessToken = await requestAccessToken(this.options.clientId);
    if (signal.aborted) return;

    report('Opening the Google Photos picker');
    const session = await createSession(this.token);
    let sessionClosed = false;

    const cleanupSession = async () => {
      if (sessionClosed) return;
      sessionClosed = true;
      await deleteSession(this.accessToken!.token, session.id).catch(() => undefined);
    };

    try {
      this.options.onPickerReady?.(`${session.pickerUri}/autoclose`);
      // Convenience only — if the popup blocker eats it, the caller's link still works.
      window.open(`${session.pickerUri}/autoclose`, '_blank', 'noopener');

      // Poll. Measured in S3: 5s interval, 1800s timeout.
      const interval = parseDurationMs(session.pollingConfig?.pollInterval, 5000);
      const deadline = Date.now() + parseDurationMs(session.pollingConfig?.timeoutIn, 30 * 60 * 1000);
      report('Waiting for you to pick photos');

      let ready = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, interval));
        if (signal.aborted) return;
        const current = await getSession(this.token, session.id);
        if (current.mediaItemsSet) {
          ready = true;
          break;
        }
      }
      if (!ready) throw new Error('Timed out waiting for photos to be picked.');

      report('Reading your selection');
      const picked = await listMediaItems(this.token, session.id);
      if (signal.aborted) return;

      const images = picked.filter(
        (item) => item.mediaFile?.baseUrl && item.mediaFile.mimeType?.startsWith('image/')
      );
      if (images.length === 0) {
        throw new Error(
          picked.length > 0
            ? 'Only videos were picked. This gallery handles images for now.'
            : 'No photos came back from the picker.'
        );
      }

      // LQIP pass. Doubles as an aspect-ratio probe: the thumbnail is fitted inside a
      // LQIP_PX box with proportions preserved, so it reveals the shape of any item whose
      // metadata is missing dimensions.
      report('Preparing previews', 0, images.length);
      const probes = await mapWithConcurrency(
        images,
        LQIP_CONCURRENCY,
        async (item) => {
          try {
            const response = await fetch(sizedBaseUrl(item.mediaFile!.baseUrl!, LQIP_PX, LQIP_PX), {
              headers: { Authorization: `Bearer ${this.token}` },
            });
            if (!response.ok) return null;
            return await probeImage(await response.blob());
          } catch {
            // A thumbnail failure shouldn't sink the whole import — fall back to a flat color.
            return null;
          }
        },
        (loaded) => report('Preparing previews', loaded, images.length),
        signal
      );
      if (signal.aborted) return;

      this.items = images.map((item, i) => {
        const probe = probes[i];
        const dims = metadataDimensions(item) ??
          (probe ? { width: probe.width, height: probe.height } : { width: 4, height: 3 });

        return {
          id: item.id,
          width: dims.width,
          height: dims.height,
          mimeType: item.mediaFile?.mimeType ?? 'image/jpeg',
          createdAt: item.createTime,
          interest: probe?.interest ?? FALLBACK_INTEREST,
          placeholder: probe?.placeholder ?? FALLBACK_PLACEHOLDER,
          resolve: (size: PhotoSize) => this.fetchBytes(item, size),
          peek: (size: PhotoSize) => this.cache.get(`${item.id}@${bucketFor(size)}`),
          release: () => {
            for (const bucket of SIZE_BUCKETS) this.cache.delete(`${item.id}@${bucket}`);
          },
        };
      });
    } finally {
      // The picked items stay valid after the session is deleted; only the session is scoped.
      await cleanupSession();
    }
  }

  private async fetchBytes(item: PickedMediaItem, size: PhotoSize): Promise<string> {
    const bucket = bucketFor(size);
    const key = `${item.id}@${bucket}`;

    const cached = this.cache.get(key);
    if (cached) return cached;

    const baseUrl = item.mediaFile?.baseUrl;
    if (!baseUrl) throw new Error('This photo has no baseUrl.');

    // Throws TokenExpiredError if we're past the cliff — surfaced to the UI as a reconnect prompt.
    const token = this.token;

    // A square box fits the image inside it with proportions preserved, so one bucket
    // number covers both orientations.
    const response = await fetch(sizedBaseUrl(baseUrl, bucket, bucket), {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 401 || response.status === 403) throw new TokenExpiredError();
    if (!response.ok) throw new Error(`Failed to load photo (${response.status})`);

    const blob = await response.blob();
    // Re-check: a long fetch can finish after another tile already cached this size.
    const raced = this.cache.get(key);
    if (raced) return raced;

    const url = URL.createObjectURL(blob);
    this.cache.set(key, url, () => URL.revokeObjectURL(url));
    return url;
  }

  list(): PhotoItem[] {
    return this.items;
  }

  dispose(): void {
    this.cache.clear();
    this.items = [];
    this.accessToken = null;
  }
}
